import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { BreadcrumbItem } from './breadcrumb.component';
import { IRoutePath, RouterService } from '@melodic-ui/services';
import { toObservable } from '@angular/core/rxjs-interop';

/**
 * Breadcrumb state is derived, not imperatively rebuilt: route crumbs, a section-level
 * override, page-scoped appended items, and keyed recurring items are all signals merged
 * by a computed. Navigation only replaces the route crumbs (and clears appended items),
 * so overrides and recurring items survive route changes without consumers having to
 * re-apply them after every navigation.
 *
 * A route can opt out of specific recurring items without the registering shell knowing:
 * `data: { hideRecurringBreadcrumbs: ['<key>'] }` on any level of the active route chain drops
 * those keys from the merge while that route is active (children of a componentless parent
 * inherit its data, so one entry covers a whole section). Declarative on purpose — the shell
 * registers once, the route says where the item does not belong, and nobody toggles anything on
 * navigation. First use: the learner app's learner switcher on the partner Reports pages, where
 * switching learners would navigate away from the report.
 */
@Injectable({
	providedIn: 'root'
})
export class BreadcrumbService {
	private _routerService: RouterService = inject(RouterService);

	/** Route-derived crumbs, replaced on every navigation. */
	private readonly _routePaths = signal<IRoutePath[]>([]);
	/** Full replacement of the route crumbs, set by section shells and cleared on their destroy. */
	private readonly _override = signal<BreadcrumbItem[] | null>(null);
	/** Page-scoped extras appended after the base crumbs; cleared on navigation. */
	private readonly _appended = signal<{ item: BreadcrumbItem; index?: number }[]>([]);
	/** Recurring items keyed by name so repeated registration replaces instead of duplicating. */
	private readonly _recurring = signal<ReadonlyMap<string, { item: BreadcrumbItem; index: number }>>(new Map());

	public readonly breadcrumbItems: Signal<BreadcrumbItem[]> = computed(() => {
		const base = this._override() ?? this._buildFromRoutes(this._routePaths());

		const items = [...base];
		for (const { item, index } of this._appended()) {
			if (index !== undefined) {
				items.splice(index, 0, item);
			} else {
				items.push(item);
			}
		}

		const hidden = this._hiddenRecurringKeys(this._routePaths());
		for (const [key, { item, index }] of this._recurring()) {
			if (!hidden.has(key)) {
				items.splice(index, 0, item);
			}
		}

		return items;
	});

	/** The recurring keys every level of the active route chain asks to hide (`data.hideRecurringBreadcrumbs`). */
	private _hiddenRecurringKeys(paths: IRoutePath[]): ReadonlySet<string> {
		const keys = new Set<string>();

		for (const route of paths) {
			const requested = route.data?.['hideRecurringBreadcrumbs'];
			if (Array.isArray(requested)) {
				for (const key of requested) {
					if (typeof key === 'string') {
						keys.add(key);
					}
				}
			}
		}

		return keys;
	}

	constructor() {
		toObservable(this._routerService.routePath).subscribe((paths) => {
			this._routePaths.set(paths);
			// Appended items are page-scoped; the destination page re-adds its own in its init.
			this._appended.set([]);
		});
	}

	/** Appends a page-scoped crumb (or inserts at `index`). Cleared automatically on navigation. */
	public addBreadcrumbItem(item: BreadcrumbItem, index?: number): void {
		this._appended.update((appended) => [...appended, { item, index }]);
	}

	/**
	 * Replaces the route-derived crumbs entirely (recurring/appended items still merge in).
	 * Persists across navigations — callers must clear it when their section is destroyed.
	 */
	public setBreadcrumbOverride(items: BreadcrumbItem[]): void {
		this._override.set(items);
	}

	public clearBreadcrumbOverride(): void {
		this._override.set(null);
	}

	/** Upserts a recurring crumb by key — safe to call repeatedly without duplicating. */
	public setRecurringBreadcrumbItem(key: string, item: BreadcrumbItem, index: number): void {
		this._recurring.update((recurring) => new Map(recurring).set(key, { item, index }));
	}

	public removeRecurringBreadcrumbItem(key: string): void {
		this._recurring.update((recurring) => {
			if (!recurring.has(key)) return recurring;
			const next = new Map(recurring);
			next.delete(key);
			return next;
		});
	}

	private _buildFromRoutes(paths: IRoutePath[]): BreadcrumbItem[] {
		const params = this._resolveRouteParams(paths);
		const crumbs = paths
			// A level whose crumbs are its parent's own array declared nothing: Angular copies a componentless
			// parent's data (and an empty-path child's parent's) onto the child, so the landing page of a
			// section like `reports` → '' would otherwise read "Reports › Reports". Compared by reference, so
			// a child that re-declares the same text on purpose still gets its crumb.
			.filter((route, i) => route.data?.['breadcrumb'] && route.data['breadcrumb'] !== paths[i - 1]?.data?.['breadcrumb'])
			.flatMap((route) => {
				const breadcrumb: BreadcrumbItem[] = route.data?.['breadcrumb'] || [];
				return breadcrumb.map((item) => ({ item, routePath: route.path }));
			});

		return crumbs.map(({ item, routePath }, i) => {
			// The last crumb is the current page: leave it linkless (rendered as plain text)
			// unless the route data explicitly provides a link.
			const isLast = i === crumbs.length - 1;
			return {
				linkIcon: item.linkIcon,
				linkText: item.linkText,
				routerLink: item.routerLink !== undefined ? this._interpolateParams(item.routerLink, params) : isLast ? undefined : routePath
			};
		});
	}

	/**
	 * Reconstructs route param values by aligning each level's raw path (`lessons/:lessonId`)
	 * with its resolved path tail (`.../lessons/456`). Deeper routes win on name collisions,
	 * so `:id` resolves to the nearest (deepest) `:id` segment in the active route chain.
	 */
	private _resolveRouteParams(paths: IRoutePath[]): ReadonlyMap<string, string> {
		const params = new Map<string, string>();

		for (const route of paths) {
			const rawSegments = route.rawPath?.split('/') ?? [];
			const resolvedSegments = route.path.split('/').slice(-rawSegments.length);

			rawSegments.forEach((segment, i) => {
				if (segment.startsWith(':') && resolvedSegments[i] !== undefined) {
					params.set(segment.slice(1), resolvedSegments[i]);
				}
			});
		}

		return params;
	}

	/** Substitutes `:param` tokens in a breadcrumb routerLink (e.g. `/enrollments/:id`) with resolved route params. */
	private _interpolateParams(routerLink: string, params: ReadonlyMap<string, string>): string {
		return routerLink.replace(/:([A-Za-z0-9_]+)/g, (token, name: string) => params.get(name) ?? token);
	}
}
